import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';

type Size = 'small' | 'medium' | 'large';

@Component({
  selector: 'app-network-status',
  template: `
    <div class="network-status" [class.always-visible]="alwaysVisible">
      <div class="indicator" [ngClass]="size">
        <div class="bars" (mouseenter)="onMouseEnter()" (mouseleave)="onMouseLeave()" (click)="handleManualRefresh()">
          <span *ngFor="let b of bars; let i = index" class="bar" [class.active]="i < level"></span>
        </div>
      </div>

      <div class="tooltip" *ngIf="showTooltipInternal && showTooltip">
        <div class="row"><span class="k">Statut :</span><span class="v">{{ statusText }}</span></div>
        <div class="row"><span class="k">Type :</span><span class="v">{{ connectionType || 'unknown' }}</span></div>
        <div class="row" *ngIf="downlink !== null"><span class="k">Debit :</span><span class="v">{{ downlink }} Mbps</span></div>
        <div class="row" *ngIf="rtt !== null"><span class="k">RTT :</span><span class="v">{{ rtt }} ms</span></div>
        <div class="row" *ngIf="saveData"><span class="k">Data saver :</span><span class="v">oui</span></div>
      </div>
    </div>
  `,
  styleUrls: ['./network-status.component.scss']
})
export class NetworkStatusComponent implements OnInit, OnDestroy {
  @Input() showTooltip = true; // équivalent showTooltip prop
  @Input() alwaysVisible = true;
  @Input() size: Size = 'small';

  // Option WebSocket (détection en temps réel sans ping)
  @Input() useWebSocket = false;
  @Input() wsUrl = ''; // ex: wss://example.com/health

  // Internal state
  isOnline = navigator.onLine;
  connectionType: string | null = null;
  downlink: number | null = null;
  rtt: number | null = null;
  saveData: boolean | null = null;
  level = 4; // 0 = offline, 1..4 quality
  bars = [0, 1, 2, 3];

  // UI helpers
  pulseEffect = false;
  showTooltipInternal = false; // hover state

  private ws: WebSocket | null = null;
  private eventDebounce: any = null;

  // keep references for removal
  private onOnline = () => this.handleOnlineOffline(true);
  private onOffline = () => this.handleOnlineOffline(false);
  private onVisibility = () => this.debouncedCheck();
  private onFocus = () => this.debouncedCheck();
  private onConnectionChange = () => this.updateFromNavigator();

  constructor(private cd: ChangeDetectorRef) { }

  ngOnInit() {
    this.updateFromNavigator();

    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.onFocus);

    if ('connection' in navigator) {
      // @ts-ignore
      const conn = (navigator as any).connection;
      try { conn.addEventListener('change', this.onConnectionChange); } catch (e) { }
    }

    if (this.useWebSocket && this.wsUrl) {
      this.openWebSocket();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    window.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('focus', this.onFocus);

    if ('connection' in navigator) {
      // @ts-ignore
      const conn = (navigator as any).connection;
      try { conn.removeEventListener('change', this.onConnectionChange); } catch (e) { }
    }

    this.closeWebSocket();
    if (this.eventDebounce) {
      clearTimeout(this.eventDebounce);
      this.eventDebounce = null;
    }
  }

  // ---------- Connection detection (no periodic ping required) ----------
  private handleOnlineOffline(online: boolean) {
    this.isOnline = online;
    this.pulse();
    this.updateLevelFromInfo();
    this.cd.markForCheck();
  }

  private debouncedCheck() {
    if (this.eventDebounce) clearTimeout(this.eventDebounce);
    this.eventDebounce = setTimeout(() => {
      this.updateFromNavigator();
      this.eventDebounce = null;
    }, 200);
  }

  private updateFromNavigator() {
    this.isOnline = navigator.onLine;
    // @ts-ignore
    const conn = (navigator as any).connection;
    if (conn) {
      this.connectionType = conn.effectiveType || null;
      this.downlink = typeof conn.downlink === 'number' ? conn.downlink : null;
      this.rtt = typeof conn.rtt === 'number' ? conn.rtt : null;
      this.saveData = !!conn.saveData;
    } else {
      this.connectionType = null;
      this.downlink = null;
      this.rtt = null;
      this.saveData = null;
    }
    this.updateLevelFromInfo();
    this.cd.markForCheck();
  }

  private updateLevelFromInfo() {
    if (!this.isOnline) { this.level = 0; return; }
    switch (this.connectionType) {
      case 'slow-2g': this.level = 1; break;
      case '2g': this.level = 2; break;
      case '3g': this.level = 3; break;
      case '4g': this.level = 4; break;
      default: this.level = 4; break;
    }
  }

  get statusText(): string {
    if (!this.isOnline) return 'offline';
    if (!this.connectionType) return 'online';
    if (this.connectionType === 'slow-2g') return 'very_slow';
    if (this.connectionType === '2g') return 'slow';
    if (this.connectionType === '3g') return 'good';
    if (this.connectionType === '4g') return 'fast';
    return 'online';
  }

  // ---------- WebSocket support (real-time, without ping) ----------
  private openWebSocket() {
    try {
      this.closeWebSocket();
      this.ws = new WebSocket(this.wsUrl);
      this.ws.onopen = () => {
        this.isOnline = true;
        this.pulse();
        this.updateLevelFromInfo();
        this.cd.markForCheck();
      };
      this.ws.onclose = () => {
        this.isOnline = false;
        this.pulse();
        this.cd.markForCheck();
        // attempt reconnect after delay
        setTimeout(() => { if (this.useWebSocket) this.openWebSocket(); }, 3000);
      };
      this.ws.onerror = () => {
        this.isOnline = false;
        this.cd.markForCheck();
      };
      this.ws.onmessage = (ev) => {
        // optional: server can send JSON with connection diagnostics
        try {
          const d = JSON.parse(ev.data);
          if (d.downlink) this.downlink = d.downlink;
          if (d.rtt) this.rtt = d.rtt;
          if (d.type) this.connectionType = d.type;
          this.updateLevelFromInfo();
          this.cd.markForCheck();
        } catch (e) {
          // ignore non-json payloads
        }
      };
    } catch (e) {
      this.ws = null;
    }
  }

  private closeWebSocket() {
    if (this.ws) {
      try { this.ws.close(); } catch (e) { }
      this.ws = null;
    }
  }

  // ---------- UI interactions ----------
  private pulse() {
    this.pulseEffect = true;
    setTimeout(() => { this.pulseEffect = false; this.cd.markForCheck(); }, 600);
  }

  onMouseEnter() { if (this.showTooltip) this.showTooltipInternal = true; }
  onMouseLeave() { this.showTooltipInternal = false; }

  handleManualRefresh() {
    this.pulse();
    if (this.useWebSocket) {
      // try to re-establish socket
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) this.openWebSocket();
    } else {
      this.updateFromNavigator();
    }
  }
}
